import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

import { FieldChrome } from './FieldChrome';

export type ClientCodePrefix = 'L' | 'NRA';

interface PrefixOption {
  prefix: ClientCodePrefix;
  label: string;
}

const PREFIXES: PrefixOption[] = [
  { prefix: 'L', label: 'Local' },
  { prefix: 'NRA', label: 'Foreign (NRA)' },
];

// A relative's code carries a household suffix (`L-1234-1`). The suffix is
// system-assigned, so it is preserved verbatim and shown read-only.
function parseCode(value: string): { prefix: ClientCodePrefix; digits: string; suffix: string } {
  const match = /^(L|NRA)-(\d*)(?:-(\d{1,3}))?$/.exec(value.trim());
  if (match) {
    return { prefix: match[1] as ClientCodePrefix, digits: match[2] ?? '', suffix: match[3] ?? '' };
  }
  return { prefix: 'L', digits: '', suffix: '' };
}

function composeCode(prefix: ClientCodePrefix, digits: string, suffix: string): string {
  const trimmedDigits = digits.trim();
  if (trimmedDigits === '') return '';
  return suffix === '' ? `${prefix}-${trimmedDigits}` : `${prefix}-${trimmedDigits}-${suffix}`;
}

const itemClasses = cn(
  'relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer select-none outline-none transition-colors duration-150',
  'hover:bg-brand-50 dark:hover:bg-white/5 data-[highlighted]:bg-brand-50 dark:data-[highlighted]:bg-white/5',
);

export interface ClientCodeFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  optional?: boolean;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
}

/** Coach-entered business client ID, e.g. "L-1234" or "NRA-19834" — a
 * prefix picker (Local/Foreign) fused to a digits-only input, mirroring
 * `PhoneNumberField`'s "picker fused to an input" pattern so the two form
 * controls read consistently. The prefix is independent local state (not
 * re-derived from `value` every render) for the same reason
 * `PhoneNumberField` keeps its dial code as local state — picking a prefix
 * before typing any digits shouldn't be discarded because `composeCode`
 * returns '' while empty. */
export function ClientCodeField({
  label = 'Client ID',
  hint,
  optional,
  value,
  onChange,
  id,
  disabled,
}: ClientCodeFieldProps): JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [prefix, setPrefix] = useState<ClientCodePrefix>(() => parseCode(value).prefix);
  const { digits, suffix } = parseCode(value);

  return (
    <FieldChrome label={label} hint={hint} optional={optional} id={fieldId}>
      <div
        className={cn(
          'flex items-stretch rounded-2xl border border-zinc-200 bg-zinc-50 transition-all duration-300 focus-within:ring-2 focus-within:ring-zinc-900/10 dark:border-white/10 dark:bg-zinc-900/80 dark:focus-within:ring-brand-500/20',
          disabled && 'opacity-50',
        )}
      >
        <SelectPrimitive.Root
          disabled={disabled ?? false}
          value={prefix}
          onValueChange={(next) => {
            const nextPrefix = next as ClientCodePrefix;
            setPrefix(nextPrefix);
            onChange(composeCode(nextPrefix, digits, suffix));
          }}
        >
          <SelectPrimitive.Trigger
            type="button"
            aria-label="Client ID prefix"
            className="flex shrink-0 items-center gap-1.5 rounded-l-2xl py-2.5 pl-3 pr-2 text-sm font-medium text-zinc-900 outline-none dark:text-zinc-100 disabled:cursor-not-allowed"
          >
            <SelectPrimitive.Value>{prefix}-</SelectPrimitive.Value>
            <SelectPrimitive.Icon>
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              sideOffset={6}
              className="z-[1100] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-soft dark:border-white/10 dark:bg-zinc-900 dark:shadow-glass-dark"
            >
              <SelectPrimitive.Viewport className="p-1.5">
                {PREFIXES.map((p) => (
                  <SelectPrimitive.Item key={p.prefix} value={p.prefix} className={itemClasses}>
                    <SelectPrimitive.ItemText>
                      {p.label} ({p.prefix}-)
                    </SelectPrimitive.ItemText>
                    <SelectPrimitive.ItemIndicator className="ml-auto">
                      <Check className="w-3.5 h-3.5 text-brand-500" />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        <div className="my-2 w-px bg-zinc-200 dark:bg-white/10" />

        <input
          id={fieldId}
          type="text"
          inputMode="numeric"
          placeholder="1234"
          value={digits}
          onChange={(e) => onChange(composeCode(prefix, e.target.value.replace(/\D/g, ''), suffix))}
          disabled={disabled}
          className="min-w-0 flex-1 rounded-r-2xl bg-transparent px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-zinc-100 dark:placeholder:text-zinc-500 disabled:cursor-not-allowed"
        />
        {suffix !== '' && (
          <span
            className="flex items-center rounded-r-2xl pr-3 text-sm font-mono text-zinc-400 dark:text-zinc-500"
            title="Household suffix — assigned automatically"
          >
            -{suffix}
          </span>
        )}
      </div>
    </FieldChrome>
  );
}
